import { ErrorMessage, Form, useFormikContext } from 'formik'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { FaChevronRight } from 'react-icons/fa'
import { MdUpload } from 'react-icons/md'

import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { useGoogleMaps } from 'src/providers/GoogleMapsProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { ParagraphSmall } from 'src/components/atoms/DesignSystem/Typography/Typography'
import { Divider } from 'src/components/atoms/Divider'
import { Image } from 'src/components/atoms/Image'
import { InternalLink, OnClickLink } from 'src/components/atoms/Link'
import { Note } from 'src/components/atoms/Note'
import { Show } from 'src/components/atoms/Show'
import {
  Heading3,
  Paragraph,
  ParagraphNoSpace
} from 'src/components/atoms/Typography'
import { FileType } from 'src/components/molecules/FileUpload'
import { FormField } from 'src/components/molecules/FormField'
import { MapAutocomplete } from 'src/components/molecules/MapAutocomplete'

import {
  useListRegionsQuery,
  useListDistrictsQuery
} from 'src/config/generated/graphql'
import { viewProjectPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { FormFieldTypes } from 'src/types/form'
import { GeoData } from 'src/types/map'
import { LandUseOptions, ProjectWorkflowState } from 'src/types/project'
import {
  geoCodePositionFromMarker,
  isValidGeo,
  matchRegionDistrictByName
} from 'src/utils/map'
import { getLandUseOptions, getPurposeOptions } from 'src/utils/projects'

import { ProjectFormValues } from './types'

export type ProjectFormProps = {
  isAdmin: boolean
  isSubmitting: boolean
  onSaveAsDraft: (formValues: ProjectFormValues) => void
  onCancel: () => void
}

export const ProjectForm = ({
  isAdmin,
  isSubmitting,
  onSaveAsDraft,
  onCancel
}: ProjectFormProps) => {
  const { isLoaded } = useGoogleMaps()
  const [address, setAddress] = useState<string>('')
  const { setFocussedHeader } = useFocusActionState()

  const { values, dirty, setFieldValue, setFieldTouched } =
    useFormikContext<ProjectFormValues>()

  const { data: regionsData } = useListRegionsQuery()
  const { data: districtsData } = useListDistrictsQuery()

  const purposeOptions = useMemo(() => getPurposeOptions(), [])
  const landUseOptions = useMemo(() => getLandUseOptions(), [])

  const handleSaveAsDraft = () => {
    onSaveAsDraft(values)
  }

  useEffect(() => {
    async function fetchGeoCode() {
      if (isValidGeo(values.Geo as GeoData)) {
        const placeCode = await geoCodePositionFromMarker(values.Geo as GeoData)
        if (placeCode.address) setAddress(placeCode.address)
      }
    }

    fetchGeoCode()
  }, [values.Geo])

  const setRegionDistrict = async (geoData?: GeoData) => {
    const regionDistrict = matchRegionDistrictByName(
      geoData,
      regionsData?.Region,
      districtsData?.District
    )

    await setFieldValue('Region', regionDistrict.region?.Id || null)

    await setFieldValue('District', regionDistrict.district?.Id || null)
  }

  const handleSelectPlace = async (data: GeoData) => {
    if (isValidGeo(data)) {
      const placeCode = await geoCodePositionFromMarker(data)
      if (placeCode.address) setAddress(placeCode.address)

      await setRegionDistrict(placeCode.geoLocation)

      await setFieldValue('Geo', placeCode.geoLocation, true)
    }
  }

  useEffect(() => {
    if (isAdmin) return

    setFocussedHeader(onCancel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, onCancel])

  const bannerUri =
    values?.HeroFile?.[0]?.Uri ||
    `${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${values.Hero}`

  return (
    <Card size='large' className='md:mb-24'>
      <Form>
        <div className='flex flex-col gap-8 mb-4'>
          <Show when={!isAdmin}>
            <div>
              <Heading3 className='font-bold!'>What is a project?</Heading3>
              <ParagraphNoSpace>
                A &apos;project&apos; involves a series of plantings with shared
                goals to restore or reforest an area. It may span multiple
                addresses, locations and years, often including various planting
                sites to achieve the restoration goal.
              </ParagraphNoSpace>
            </div>
          </Show>
          <Note type='infoNew' className='my-0!'>
            This information will be visible to the public on the Trees That
            Count website when you start receiving trees. You can{' '}
            <InternalLink
              openInNewTab
              href={viewProjectPath({
                routeParams: {
                  uri: 'bhmetrestoration'
                }
              })}
            >
              view an example of a project here.
            </InternalLink>
          </Note>
          <Paragraph>
            All fields are required unless indicated as optional.
          </Paragraph>
        </div>
        <FormField
          id='Name'
          label='1. Project name'
          description="What is the name of your planting project? You'll be able to change this later."
          type={FormFieldTypes.text}
        />
        <FormField
          id='Description'
          label='2. Project description'
          description='Describe your project in detail. Provide a story - the background, objectives and goals and anything that will provide a deep and wide context for it. Remember, visitors and funders will be reading this.'
          type={FormFieldTypes.textarea}
          helpText='Tip: Write this up in another document, copy and paste it here.'
          rows={4}
          resize
        />
        <FormField
          id='NumberOfProperties'
          label='3. Number of discrete locations for your project'
          description="How many locations does your project include? Projects can include locations that are not all on the same physical address. You'll be able to change this later."
          formFieldClassName='w-1/3 md:w-1/6'
          options={[
            ...Array.from({ length: 10 }, (_, i) => (i + 1).toString()),
            '10+'
          ].map(i => ({ label: i, value: i }))}
          shouldParseInt={false}
          type={FormFieldTypes.select}
        />
        <FormField
          id='Area'
          label='4. Size of the project in hectares'
          description={
            values.NumberOfProperties === '1'
              ? "What's the overall area or size of the planting or restoration area for this project? Please only include the size of the planting or restoration area if it's a portion of a larger location."
              : "What's the overall area or size of the restoration area for this project including all locations? Please only include the size of the restoration area across the locations in the project."
          }
          options={[
            { label: 'Less than 2 Ha', value: '< 2 Ha' },
            { label: '2 to 5 Ha', value: '2-5 Ha' },
            { label: '5 to 10 Ha', value: '5-10 Ha' },
            { label: '10 to 20 Ha', value: '10-20 Ha' },
            { label: '20 to 50 Ha', value: '20-50 Ha' },
            { label: '50 to 100 Ha', value: '50-100 Ha' },
            { label: 'Over 100 Ha', value: 'Over 100 Ha' }
          ]}
          formFieldClassName='w-1/2 md:w-1/4'
          type={FormFieldTypes.select}
        />
        <FormField
          id='PlantingTimeframes'
          label='5. Planting timeframes'
          description='How long this project is intended to run for.'
          options={[
            { label: 'Up to 1 yr', value: 'Up to 1 yr' },
            { label: 'Up to 5 yrs', value: 'Up to 5 yrs' },
            { label: 'Up to 10 yrs', value: 'Up to 10 yrs' },
            { label: '10 yrs or more', value: '10 yrs or more' }
          ]}
          formFieldClassName='w-1/2 md:w-1/4'
          type={FormFieldTypes.select}
        />
        <FormField
          id='Purpose'
          label='6. Purpose of this project'
          description='Which of the following represents the purpose of this project? Select up to three most relevant.'
          type={FormFieldTypes.checkboxGroup}
          options={purposeOptions.map(v => ({
            ...v,
            disabled:
              values.Purpose.length === 3 && !values.Purpose.includes(v.value)
          }))}
        />
        <FormField
          id='LandUse'
          label='7. Land use type'
          description={
            values.NumberOfProperties === '1'
              ? "What's the land currently used for? Check all that apply."
              : "What's the land currently used for? Check all that apply for the different locations for this project."
          }
          type={FormFieldTypes.checkboxGroup}
          options={landUseOptions}
          conditionForConditionalComponent={values.LandUse.includes(
            LandUseOptions.Other
          )}
          conditionalComponent={
            <FormField
              id='LandUseOther'
              label='Other (please specify)'
              type={FormFieldTypes.text}
            />
          }
          conditionalComponentId='Project.PlantingTimeframes'
        />
        {isLoaded ? (
          <>
            <MapAutocomplete
              id='Geo'
              label='8. Base project address'
              description="Where will your project be primarily based? We'll only display the general location to the public."
              value={address}
              onSelectPlace={handleSelectPlace}
              onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                setAddress(e.target.value)
                await setFieldValue('Geo', null)
                setFieldTouched('Geo', true, true)
              }}
            />
            <ErrorMessage
              name='Geo'
              render={(msg: string) => (
                <span className='form-error-message'>{msg}</span>
              )}
            />
          </>
        ) : null}
        <div className='mb-4'>
          <label className='form-label'>9. Photos</label>
          <ParagraphSmall className='mb-4! form-description-text'>
            Add photos to represent your project. Once tree requests are
            approved, these will be displayed on the website for visitors and
            potential funders to enjoy. You&apos;ll be able to add/change them
            out later.
          </ParagraphSmall>
          <div className='border-dashed border border-oliveAsh rounded-md p-5 mb-5'>
            <FormField
              id='HeroFile'
              className='mb-0'
              fileType={FileType.image}
              description='This photo will be the first photo that will be featured for your project.'
              label='Hero image'
              maxFiles={1}
              renderDropzone={false}
              renderFilesList={false}
              replaceFiles
              type={FormFieldTypes.file}
              uploadButton={
                <ButtonRebrand variant='forest'>
                  <MdUpload className='mr-2' />
                  <span>Upload hero</span>
                </ButtonRebrand>
              }
            />
            <div className='relative h-36 mt-4'>
              <Image
                src={bannerUri}
                alt={`Hero image for ${values?.Name}`}
                fill
                className='object-contain object-left'
              />
            </div>
          </div>
          <div className='border-dashed border border-oliveAsh rounded-md p-5 md:mb-10 mb-5'>
            <FormField
              id='ProjectAssets'
              label='Other project photos'
              className='mb-0'
              description='If this is your first project, help show your story with photos of your group out planting and before photos that can be used to compare over the course of your project. '
              type={FormFieldTypes.file}
              fileType={FileType.image}
              renderDropzone={false}
              renderFilesList
              renderFilesAsList={false}
              uploadButton={
                <ButtonRebrand variant='forest'>
                  <MdUpload className='mr-2' />
                  <span>Upload photos</span>
                </ButtonRebrand>
              }
            />
          </div>
        </div>

        <FormField
          id='WebsiteLinks'
          label='10. Related websites and links (optional)'
          description='Do you have related information to give us more context? e.g. Links to websites, videos, news articles, your Facebook page, website and so on.'
          placeholder='https://website.com'
          type={FormFieldTypes.textArray}
        />
        <FormField
          id='OtherLandArea'
          label='11. Other land available for planting'
          formFieldClassName='w-1/2 md:w-1/3'
          description='Beyond the project, do you have other land that could be used for native tree planting?'
          type={FormFieldTypes.select}
          options={[
            { label: 'Not at the moment', value: 'Not at the moment' },
            { label: 'Less than 2 Ha', value: '< 2 Ha' },
            { label: '2 to 5 Ha', value: '2-5 Ha' },
            { label: '5 to 10 Ha', value: '5-10 Ha' },
            { label: '10 to 20 Ha', value: '10-20 Ha' },
            { label: '20 to 50 Ha', value: '20-50 Ha' },
            { label: '50 to 100 Ha', value: '50-100 Ha' },
            { label: 'Over 100 Ha', value: 'Over 100 Ha' }
          ]}
        />
        <Divider />
        <div className='flex flex-row justify-between mb-8'>
          <OnClickLink
            color='forestDeep'
            className='underline self-center'
            onClick={onCancel}
          >
            Cancel
          </OnClickLink>
          <div className='flex gap-2'>
            {/* Also don't show if application is already submitted */}
            <Show
              when={
                !isAdmin &&
                values.WorkflowState !== ProjectWorkflowState.Submitted
              }
            >
              <ButtonRebrand
                variant='outline'
                disabled={isSubmitting || !dirty}
                onClick={handleSaveAsDraft}
              >
                {values.Id ? 'Save changes to draft' : 'Save as draft'}
              </ButtonRebrand>
            </Show>
            <ButtonRebrand
              variant='lime'
              disabled={isSubmitting || !dirty}
              type='submit'
              className='gap-2'
            >
              Submit
              <FaChevronRight size={12} />
            </ButtonRebrand>
          </div>
        </div>
      </Form>
    </Card>
  )
}
